import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import {
  ACTION_POPUP_CONTENT_INNER_CLASS,
} from '../../../components/popup/popupLayoutSpec';
import Heading from '../../../components/common/Typography/Heading';
import { disableScroll, enableScroll } from '../../../utils/scrollUtils';
import { useInlineRowActions, ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useInlineRowActions';
import { useConfigurationListPagination } from '../../../Hooks/useConfigurationListPagination';
import {
  AdminPageTitle,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminSupportingText,
  AdminHeaderAction,
  AdminRowMenuItem,
  AdminRowActionsButton,
  AdminFixedRowActionsMenu,
  AdminRowMenuIcon,
  ADMIN_PAGE_TITLE_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import {
  FORM_INVENTORY_ROUTE,
  LIST_PREVIEW_COUNT,
  MESSAGES,
  buildSampleFormInventory,
  formatFormInventoryDate,
  getFieldCount,
} from '../../../data/formInventoryConfig';

const tableCols =
  'minmax(110px, 0.7fr) minmax(160px, 1.3fr) minmax(120px, 0.9fr) minmax(120px, 0.9fr) minmax(120px, 0.9fr) minmax(140px, 1.1fr) minmax(110px, 0.85fr) minmax(72px, 0.45fr) minmax(100px, 0.7fr) minmax(100px, 0.7fr) minmax(56px, 0.35fr)';

const TABLE_MIN_WIDTH = '1280px';

const PREVIEW_TABLE_COLS = 'auto auto';

const PreviewValuesModal = ({ title, rows, onClose }) => {
  useEffect(() => {
    disableScroll();
    return () => enableScroll();
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/20 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="form-inventory-preview-modal-title"
    >
      <div className="flex-shrink-0 flex items-center justify-end h-[60px] sm:h-[80px] pr-4 sm:pr-6 z-10">
        <button
          type="button"
          onClick={onClose}
          className="flex items-center justify-center text-offblack"
          aria-label="Close"
        >
          <i className="fi fi-rs-cross text-sm cursor-pointer" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto bg-white rounded-t-xl min-h-0">
        <div className="grid min-h-full place-items-center py-10 px-4">
          <div className="mx-auto max-w-[calc(100vw-2rem)] hidden-scrollbar lg:pb-0 relative">
            <div id="form-inventory-preview-modal-title">
              <Heading
                variant="h6"
                className="flex items-center justify-center text-center text-offblack"
              >
                {title}
              </Heading>
            </div>
            <div className={`${ACTION_POPUP_CONTENT_INNER_CLASS} !w-max max-w-full mx-auto`}>
              <div className="grid text-offblack" style={{ gridTemplateColumns: PREVIEW_TABLE_COLS }}>
                <div className="flex items-center whitespace-nowrap py-3 pr-12 border-b border-lightgray">
                  <AdminTableHeader title="#">#</AdminTableHeader>
                </div>
                <div className="flex items-center whitespace-nowrap py-3 border-b border-lightgray">
                  <AdminTableHeader title="Value">Value</AdminTableHeader>
                </div>
                {rows.map((row, index) => {
                  const isLast = index === rows.length - 1;
                  const cellBorder = isLast ? '' : 'border-b border-lightgray';
                  return (
                    <React.Fragment key={`${row}-${index}`}>
                      <div className={`flex items-center whitespace-nowrap py-3 pr-12 ${cellBorder}`}>
                        <AdminTableMeta title={String(index + 1)}>{index + 1}</AdminTableMeta>
                      </div>
                      <div className={`flex items-center min-w-0 py-3 ${cellBorder}`}>
                        <AdminTableBody className="break-words block" title={row}>
                          {row}
                        </AdminTableBody>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PreviewListCell = ({ items, emptyLabel = '—', onViewAll }) => {
  if (!items || items.length === 0) {
    return (
      <AdminTableBody className="truncate block" title={emptyLabel}>
        {emptyLabel}
      </AdminTableBody>
    );
  }

  const preview = items.slice(0, LIST_PREVIEW_COUNT).join(', ');
  const extraCount = items.length - LIST_PREVIEW_COUNT;
  const fullText = items.join(', ');

  return (
    <AdminTableBody className="block min-w-0 w-full" title={fullText}>
      <span className="flex items-center min-w-0 w-full gap-1">
        <span className="min-w-0 truncate">{preview}</span>
        {extraCount > 0 ? (
          <button
            type="button"
            className="text-primaryblue hover:underline shrink-0 whitespace-nowrap"
            onClick={(e) => {
              e.stopPropagation();
              onViewAll?.();
            }}
          >
            +{extraCount} more
          </button>
        ) : null}
      </span>
    </AdminTableBody>
  );
};

const FormInventory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [forms, setForms] = useState(() => buildSampleFormInventory());
  const [feedback, setFeedback] = useState('');
  const [viewAllModal, setViewAllModal] = useState(null);

  const {
    rowActionsOpen,
    rowActionsRefs,
    tableScrollRef,
    rowActionsMenuLayout,
    rowActionsMenuRef,
    rowActionsMenuPlacementReady,
    toggleRowActions,
    closeRowActions,
  } = useInlineRowActions(ROW_ACTIONS_MENU_HEIGHT.DOUBLE);

  useEffect(() => {
    const incoming = location.state?.forms;
    const message = location.state?.feedback;
    if (Array.isArray(incoming) || message) {
      if (Array.isArray(incoming)) setForms(incoming);
      if (message) setFeedback(message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const {
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedItems: paginatedForms,
    rangeStart,
    rangeEnd,
    totalCount,
    showPagination,
  } = useConfigurationListPagination(forms);

  const openFormView = (form) => {
    navigate(`${FORM_INVENTORY_ROUTE}/${encodeURIComponent(form.id)}`, {
      state: { form, forms },
    });
  };

  const openFormEdit = (form) => {
    navigate(`${FORM_INVENTORY_ROUTE}/${encodeURIComponent(form.id)}/edit`, {
      state: { form, forms },
    });
  };

  return (
    <AdminLayout>
      <div className="min-w-0">
        <div className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
          <AdminPageTitle className="!mb-0">Form Inventory</AdminPageTitle>
          <AdminHeaderAction
            size="lg"
            onClick={() => navigate(`${FORM_INVENTORY_ROUTE}/add`, { state: { forms } })}
          >
            Add Form
          </AdminHeaderAction>
        </div>

        {feedback ? (
          <AdminSupportingText className="text-freshgreen mb-4">{feedback}</AdminSupportingText>
        ) : null}

        <DashboardMainContainer className={`${ADMIN_TABLE_CLASS} max-w-full min-w-0`}>
          <div ref={tableScrollRef} className="overflow-x-auto">
            {forms.length === 0 ? (
              <div className="py-10 px-4 text-center">
                <AdminSupportingText className="text-mediumgray">
                  {MESSAGES.EMPTY_TABLE}
                </AdminSupportingText>
              </div>
            ) : (
              <>
                <div
                  className="grid w-full py-3 text-offblack border-b border-lightgray px-4 gap-x-3 gap-y-2"
                  style={{ gridTemplateColumns: tableCols, minWidth: TABLE_MIN_WIDTH }}
                >
                  {[
                    'Form ID',
                    'Form Name',
                    'Category',
                    'Owning Module',
                    'Used By',
                    'Entry Points',
                    'Modes',
                    'Field Count',
                    'Date Added',
                    'Last Updated',
                    'Actions',
                  ].map((heading) => (
                    <div
                      key={heading}
                      className={`flex items-center min-w-0 ${heading === 'Actions' ? 'justify-center shrink-0' : ''}`}
                    >
                      <AdminTableHeader className="truncate" title={heading}>
                        {heading}
                      </AdminTableHeader>
                    </div>
                  ))}
                </div>

                {paginatedForms.map((form) => (
                  <div
                    key={form.id}
                    className="grid w-full py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-3 gap-y-2"
                    style={{ gridTemplateColumns: tableCols, minWidth: TABLE_MIN_WIDTH }}
                  >
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableMeta className="truncate block" title={form.id}>
                        {form.id}
                      </AdminTableMeta>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody className="truncate block" title={form.formName}>
                        {form.formName}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody className="truncate block" title={form.category}>
                        {form.category || '—'}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody className="truncate block" title={form.owningModule}>
                        {form.owningModule || '—'}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <PreviewListCell
                        items={form.usedBy || []}
                        onViewAll={() =>
                          setViewAllModal({ title: 'Used By', rows: form.usedBy || [] })
                        }
                      />
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <PreviewListCell
                        items={form.entryPoints || []}
                        onViewAll={() =>
                          setViewAllModal({ title: 'Entry Points', rows: form.entryPoints || [] })
                        }
                      />
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <PreviewListCell
                        items={form.modes || []}
                        onViewAll={() =>
                          setViewAllModal({ title: 'Modes', rows: form.modes || [] })
                        }
                      />
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody className="truncate block" title={String(getFieldCount(form))}>
                        {getFieldCount(form)}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody
                        className="truncate block"
                        title={formatFormInventoryDate(form.dateAdded)}
                      >
                        {formatFormInventoryDate(form.dateAdded)}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody
                        className="truncate block"
                        title={formatFormInventoryDate(form.lastUpdated)}
                      >
                        {formatFormInventoryDate(form.lastUpdated)}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center justify-center shrink-0 overflow-visible">
                      <div
                        className="relative inline-flex"
                        ref={(el) => {
                          rowActionsRefs.current[form.id] = el;
                        }}
                      >
                        <AdminRowActionsButton
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRowActions(form.id);
                          }}
                          aria-expanded={!!rowActionsOpen[form.id]}
                          title="Actions"
                          aria-label="Row actions"
                        >
                          <AdminRowMenuIcon />
                        </AdminRowActionsButton>
                        {rowActionsOpen[form.id] && rowActionsMenuLayout ? (
                          <AdminFixedRowActionsMenu
                            layout={rowActionsMenuLayout}
                            menuRef={rowActionsMenuRef}
                            placementReady={rowActionsMenuPlacementReady}
                          >
                            <AdminRowMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                closeRowActions();
                                openFormView(form);
                              }}
                            >
                              View
                            </AdminRowMenuItem>
                            <AdminRowMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                closeRowActions();
                                openFormEdit(form);
                              }}
                            >
                              Edit
                            </AdminRowMenuItem>
                          </AdminFixedRowActionsMenu>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalCount}
          currentPage={currentPage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>

      {viewAllModal ? (
        <PreviewValuesModal
          title={viewAllModal.title}
          rows={viewAllModal.rows}
          onClose={() => setViewAllModal(null)}
        />
      ) : null}
    </AdminLayout>
  );
};

export default FormInventory;
